import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRouter} from 'next/router';
import cookie from 'react-cookies';
import dynamic from 'next/dynamic';
import {initializeStore} from '../../../store/store';
import productActions from '../../../store/actions/productActions';
import BreadCrumbs from '../../../components/UI/BreadCrumbs';
import Carousel from '../../../components/UI/Carousel';
import ProductData from '../../../components/Product/ProductData';
import ProductAdditional from '../../../components/Product/ProductAdditional';
import SimilarProducts from '../../../components/Product/SimilarProducts';

import orderActions from '../../../store/actions/order.actions';
import NotFound from '../../../components/UI/NotFound';
import MetaTags from '../../../components/MetaTags/MetaTags';
import Preloader from '../../../components/UI/Preloader';
import helpers from '../../../helpers/helpers';
import usePublicSocket, { SOCKET_PRODUCT_BID } from '../../../customHooks/usePublicSocket';
import { getMetaDescription, getMetaTitle } from '../../../helpers/getProductMeta';
import ShareBtn from '../../../components/ShareBtn/ShareBtn';
import urlHelper from '../../../helpers/url.helper';
//import Icon from '../../components/UI/Icon';

const Lightbox = dynamic(() => import('../../../components/Lightbox/Lightbox'), {ssr: false});

const Product = () => {
  const dispatch = useDispatch();
  const {currentProduct} = useSelector((state) => state.products);
  const {languageIsLoading, language} = useSelector((state) => state.language);
  const similarProducts = useSelector((state) => state.products.similarProducts);
  const {token} = useSelector((state) => state.auth);
  const router = useRouter();
  const url = router.asPath.split('/');
  const productId = url[url.length - 1];

  const {getTranslationText} = helpers;

  const [crumbs, setCrumbs] = useState([{
    title: getTranslationText('general.home') || 'Home',
    href: '/',
  }]);

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    photoIndex: null,
    images: [],
  });

  const [quantity, setQuantity] = useState(1);
  const [bid, setBid] = useState(0);

  useEffect(() => {
    if (currentProduct.categories) {
      let categoryAccum = '/category';
      const links = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i <= currentProduct.categories.length - 1; ++i) {
        links[i] = `${categoryAccum}/${currentProduct.categories[i].path.split('_')[0]}`;
        categoryAccum = `${categoryAccum}/${currentProduct.categories[i].path.split('_')[0]}`;
      }
      setCrumbs([
        {
          title: (cookie.load('lang_market') || process.env.DEFAULT_LANG) === 'ru-RU' ? 'Главная' : 'Home',
          href: '/',
        },
        ...currentProduct.categories.map((el, index) => ({
          title: el.name,
          href: links[index],
          as: links[index],
        }))]);
    }
  }, [currentProduct]);

  useEffect(() => {
    if (productId) {
      dispatch(productActions.getSimilarProducts(productId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  usePublicSocket(SOCKET_PRODUCT_BID);

  const userClickOnTagHandler = (e, value) => {
    e.stopPropagation();
    e.preventDefault();
    router.push('/search', `/search?tag=${value}`, {shallow: true});
  };

  const onComplete = () => {
    const orderModel = {};
    if (currentProduct.type === 0) {
      orderModel.product_id = currentProduct.id;
      orderModel.currency_id = currentProduct.price.id;
      orderModel.quantity = quantity;

      dispatch(orderActions.createOrder(orderModel, token, language)).then((res) => {
        if (res?.order?.link) {
          router.push({
            pathname: '/cabinet/deals',
            query: {order: res.order.link},
            shallow: true,
          });
        }
      });
    } else if (currentProduct.type === 10) {
      orderModel.product_id = currentProduct.id;
      orderModel.bid = bid;

      dispatch(orderActions.makeBid(orderModel, token, language));
    }
  };

  const onImageClick = (photoIndex) => () => {
    const images = currentProduct.photos.map((item) => item.origin);
    setLightbox({
      isOpen: true,
      photoIndex,
      images,
    });
  };

  const onCloseLightbox = () => {
    setLightbox((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  return (
    <>
      <MetaTags
        metaTitle={getMetaTitle(currentProduct, language)}
        metaDescription={getMetaDescription(currentProduct, language)}
      />
      <div className="product__wrapper">
        {languageIsLoading && <Preloader/>}
        <BreadCrumbs crumbs={crumbs}/>
        {
          Object.keys(currentProduct).length === 0 ? <NotFound title="404 Product not found"/> : (
            <>
              <div className="container">
                <div className="product__container">
                  {/*<div>*/}
                  {/*  <div className="product__data__views__mob">*/}
                  {/*    {currentProduct.visits}*/}
                  {/*    {' '}*/}
                  {/*    <Icon type="eye" />*/}
                  {/*  </div>*/}
                  {/*</div>*/}
                  <Carousel images={currentProduct.photos} onImageClick={onImageClick}/>
                  <ProductData
                    currentProduct={currentProduct}
                    userClickOnTagHandler={userClickOnTagHandler}
                    onPurchase={onComplete}
                    quantity={quantity}
                    quantityHandle={setQuantity}
                    bid={bid}
                    bidHandle={setBid}
                  />
                  <ShareBtn 
                    modalClassName="product__share-btn"
                    linkText={`${urlHelper.getBasePath(router)}${router.asPath}`}
                    iframeText={`<iframe src="${urlHelper.getBasePath(router)}/widget/product/${router.query.id}" frameborder="0" width="100%" height="240"></iframe>`}
                  />
                </div>
              </div>
              <ProductAdditional
                descriptionInfo={currentProduct.description}
              />
              <SimilarProducts products={similarProducts}/>
            </>
          )
        }
      </div>
      {lightbox.isOpen && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.photoIndex}
          onClose={onCloseLightbox}
        />
      )}
    </>
  );
};

export async function getServerSideProps({query, req}) {
  const reduxStore = initializeStore();
  const {dispatch} = reduxStore;

  const {parseCookie, clearSsrHeaders} = helpers;

  const params = {
    include: 'photos,tags,description,delivery,attributes,options,categories,auction_bids',
    product_in_order: query?.product_in_order ? query.product_in_order : 0,
  };
  const headers = clearSsrHeaders(req?.headers) ?? {};
  const parsedCookies = parseCookie(req?.headers.cookie);
  headers['accept-language'] = parsedCookies?.lang_market || process.env.DEFAULT_LANG || 'en-US';

  if (!query.id) {
    return {
      notFound: true
    }
  }

  // eslint-disable-next-line max-len
  await dispatch(productActions.getCurrentProduct(query.id, params, parsedCookies?.access_token_market || null, headers));

  return {
    props: {
      initialReduxState: {products: reduxStore.getState().products},
    },
  };
}

export default Product;
